import { Capacitor } from "@capacitor/core";
import Vue from "vue";

// Capacitor's bridge detection can briefly report `web` while the WebView is
// starting. The mobile build marker makes the bundled APK deterministic while
// keeping the normal production website in browser mode.
const isNativeMobileBuild = process.env.VUE_APP_NATIVE_MOBILE === "true";

export const isNativeMobileApp = isNativeMobileBuild || (window.self === window.top && Capacitor.isNativePlatform());
export const useLiveMobilePortal = isNativeMobileBuild && process.env.VUE_APP_MOBILE_LIVE_PORTAL === "true";
export const embeddedMobileApp = Vue.observable({ active: false });
let embeddedNonce = "";
let pullToRefreshInstalled = false;

function installEmbeddedPullToRefresh() {
  if (pullToRefreshInstalled) return;
  pullToRefreshInstalled = true;
  let startX = 0;
  let startY = 0;
  let distance = 0;
  let tracking = false;
  const indicator = document.createElement("div");
  indicator.setAttribute("role", "status");
  indicator.setAttribute("aria-live", "polite");
  Object.assign(indicator.style, {
    alignItems: "center",
    background: "var(--v-surface-base, var(--v-background-base, #fff))",
    border: "1px solid var(--v-primary-base)",
    borderRadius: "18px",
    boxShadow: "0 4px 12px rgba(0,0,0,.16)",
    color: "var(--v-primary-base)",
    display: "none",
    fontSize: "12px",
    fontWeight: "600",
    height: "36px",
    justifyContent: "center",
    left: "50%",
    padding: "0 16px",
    position: "fixed",
    top: "8px",
    transform: "translateX(-50%)",
    zIndex: "9999",
  });
  document.body.appendChild(indicator);

  const atTop = () => (document.scrollingElement || document.documentElement).scrollTop <= 0;
  const overlayOpen = () => Boolean(document.querySelector(".v-dialog--active, .v-menu__content--active"));
  const reset = () => {
    tracking = false;
    distance = 0;
    indicator.style.display = "none";
  };

  document.addEventListener("touchstart", (event) => {
    if (overlayOpen() || !atTop() || !event.touches || event.touches.length !== 1) return;
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    distance = 0;
    tracking = true;
  }, { passive: true });
  document.addEventListener("touchmove", (event) => {
    if (!tracking || !event.touches || event.touches.length !== 1 || !atTop()) return reset();
    const deltaY = event.touches[0].clientY - startY;
    const deltaX = Math.abs(event.touches[0].clientX - startX);
    if (deltaY <= 0 || deltaX > deltaY) return reset();
    distance = Math.min(96, Math.round(deltaY * 0.45));
    indicator.textContent = distance >= 64 ? "Release to refresh" : "Pull to refresh";
    indicator.style.display = distance > 4 ? "flex" : "none";
    if (distance > 4 && event.cancelable) event.preventDefault();
  }, { passive: false });
  document.addEventListener("touchend", () => {
    if (!tracking) return;
    if (distance < 64) return reset();
    tracking = false;
    indicator.textContent = "Refreshing…";
    window.location.reload();
  }, { passive: true });
  document.addEventListener("touchcancel", reset, { passive: true });
}

export function listenForEmbeddedAppContext(router) {
  if (window.self === window.top) return;
  const portalHost = window.location.hostname.toLowerCase();
  const inferredParentOrigin = portalHost.includes("storesyncpos.com")
    ? "https://mobile.storesyncpos.com"
    : portalHost.includes("autorepairdesk.com")
      ? "https://mobile.autorepairdesk.com"
      : "https://mobile.repairnestpos.com";
  const parentOrigin = process.env.VUE_APP_MOBILE_PARENT_ORIGIN || inferredParentOrigin;
  window.addEventListener("message", (event) => {
    if (event.source !== window.parent || event.origin !== parentOrigin) return;
    const message = event.data || {};
    if (message.type === "repairnest:hello" && typeof message.nonce === "string" && message.nonce.length >= 16) {
      embeddedNonce = message.nonce;
      embeddedMobileApp.active = true;
      document.documentElement.classList.add("embedded-mobile-app");
      installEmbeddedPullToRefresh();
      window.parent.postMessage({ type: "repairnest:ready", nonce: message.nonce, path: router.currentRoute.fullPath }, parentOrigin);
      if (["/login", "/logout"].includes(router.currentRoute.path)) {
        window.parent.postMessage({ type: "repairnest:navigated", nonce: message.nonce, path: router.currentRoute.fullPath }, parentOrigin);
      }
    } else if (embeddedMobileApp.active && message.nonce === embeddedNonce && message.type === "repairnest:navigate" && typeof message.path === "string" && message.path.startsWith("/") && !message.path.startsWith("//")) {
      router.push(message.path).catch(() => {});
    } else if (embeddedMobileApp.active && message.nonce === embeddedNonce && message.type === "repairnest:logout") {
      localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}token`);
      localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}organizationId`);
      window.parent.postMessage({ type: "repairnest:logged-out", nonce: message.nonce }, parentOrigin);
    }
  });
  router.afterEach((to) => {
    if (embeddedMobileApp.active) window.parent.postMessage({ type: "repairnest:navigated", nonce: embeddedNonce, path: to.fullPath }, parentOrigin);
  });
}

export function setMobileAppContext() {
  if (isNativeMobileApp) document.documentElement.classList.add("native-mobile-app");
}
